import { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Stack,
  Typography,
  Link,
  Alert,
  InputAdornment,
  IconButton,
} from "@mui/material";

import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

import { post_login, post_register } from "../../client/paipan";

// 用户名：只允许字母、数字、下划线，3-20 位
const USERNAME_REGEX = /^[a-zA-Z0-9_]{3,20}$/;
// 密码：6-20 位，不允许包含 SQL 注入危险字符 ' " ; -- /* */
const PASSWORD_REGEX = /^[^'";\-/\\]{6,20}$/;

export default function LoginDialog({ open, onClose, onLoginSuccess }) {
  const [mode, setMode] = useState("login"); // "login" | "register"
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  function handleClose() {
    setMode("login");
    setUsername("");
    setPassword("");
    setConfirmPassword("");
    setShowPassword(false);
    setShowConfirm(false);
    setError("");
    setSuccess("");
    onClose();
  }

  // 实时校验提示
  function getUsernameError() {
    if (!username) return "";
    if (!USERNAME_REGEX.test(username)) {
      return "用户名只能包含字母、数字、下划线，长度 3-20";
    }
    return "";
  }

  function getPasswordError() {
    if (!password) return "";
    if (!PASSWORD_REGEX.test(password)) {
      return "密码长度 6-20，不能包含 ' \" ; - / 等危险字符";
    }
    return "";
  }

  function getConfirmError() {
    if (!confirmPassword) return "";
    if (confirmPassword !== password) {
      return "两次输入的密码不一致";
    }
    return "";
  }

  function validate() {
    if (!username.trim()) {
      return "请输入用户名";
    }
    if (!USERNAME_REGEX.test(username.trim())) {
      return "用户名只能包含字母、数字、下划线，长度 3-20";
    }
    if (!password) {
      return "请输入密码";
    }
    if (!PASSWORD_REGEX.test(password)) {
      return "密码长度 6-20，不能包含 ' \" ; - / 等危险字符";
    }
    if (mode === "register") {
      if (!confirmPassword) {
        return "请确认密码";
      }
      if (password !== confirmPassword) {
        return "两次输入的密码不一致";
      }
    }
    return "";
  }

  async function handleSubmit() {
    const errMsg = validate();
    if (errMsg) {
      setError(errMsg);
      setSuccess("");
      return;
    }
    setError("");
    setSuccess("");
    setLoading(true);

    try {
      let res;
      if (mode === "login") {
        res = await post_login({ username: username.trim(), password });
        if (res && res.token) {
          localStorage.setItem("token", res.token);
          localStorage.setItem("user", JSON.stringify(res.user || { username: username.trim() }));
          onLoginSuccess(res.user || { username: username.trim() });
          handleClose();
        } else {
          setError(res?.message || "登录失败，请重试");
        }
      } else {
        res = await post_register({ username: username.trim(), password });
        if (res && res.message === "注册成功") {
          setSuccess("注册成功，请登录");
          setPassword("");
          setConfirmPassword("");
          setMode("login");
        } else {
          setError(res?.message || "注册失败，请重试");
        }
      }
    } catch (err) {
      setError("网络错误，请稍后重试");
    } finally {
      setLoading(false);
    }
  }

  function toggleShowPassword() {
    setShowPassword((prev) => !prev);
  }

  function toggleShowConfirm() {
    setShowConfirm((prev) => !prev);
  }

  const passwordAdornment = (
    <InputAdornment position="end">
      <IconButton onClick={toggleShowPassword} edge="end" tabIndex={-1}>
        {showPassword ? <VisibilityOff /> : <Visibility />}
      </IconButton>
    </InputAdornment>
  );

  const usernameError = getUsernameError();
  const passwordError = getPasswordError();
  const confirmError = mode === "register" ? getConfirmError() : "";

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>{mode === "login" ? "登录" : "注册"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          {success && <Alert severity="success">{success}</Alert>}
          <TextField
            label="用户名"
            variant="outlined"
            fullWidth
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            error={!!usernameError}
            helperText={usernameError || "字母/数字/下划线，3-20 位"}
            autoFocus
          />
          <TextField
            label="密码"
            type={showPassword ? "text" : "password"}
            variant="outlined"
            fullWidth
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && mode === "login") handleSubmit();
            }}
            error={!!passwordError}
            helperText={passwordError || "长度 6-20，不含 ' \" ; - / 等字符"}
            InputProps={{ endAdornment: passwordAdornment }}
          />
          {mode === "register" && (
            <TextField
              label="确认密码"
              type={showConfirm ? "text" : "password"}
              variant="outlined"
              fullWidth
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSubmit();
              }}
              error={!!confirmError}
              helperText={confirmError || "请再次输入密码"}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={toggleShowConfirm} edge="end" tabIndex={-1}>
                      {showConfirm ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          )}
          <Typography variant="body2" sx={{ textAlign: "center" }}>
            {mode === "login" ? (
              <>
                还没有账号？{" "}
                <Link
                  component="button"
                  variant="body2"
                  onClick={() => {
                    setMode("register");
                    setError("");
                    setSuccess("");
                  }}
                >
                  去注册
                </Link>
              </>
            ) : (
              <>
                已有账号？{" "}
                <Link
                  component="button"
                  variant="body2"
                  onClick={() => {
                    setMode("login");
                    setError("");
                    setSuccess("");
                  }}
                >
                  去登录
                </Link>
              </>
            )}
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} color="inherit">
          取消
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
        >
          {loading ? "请稍候..." : mode === "login" ? "登录" : "注册"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
